import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Bell, Check, CheckCheck, FileText, MessageSquareWarning, ClipboardCheck, MapPin, Sparkles,
    IndianRupee, Pencil, Ban, ShieldCheck, ThumbsUp, ThumbsDown, Tag, ArrowRightCircle, Upload,
    CheckCircle2, Rocket, Megaphone, Clock, UserX, Trash2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Phase 48 — Header bell for in-app notifications (assigned Applications,
// Complaints, Site Surveys, Leads). Polls unread-count every 45s to keep the
// badge fresh without hammering the API. On open we hydrate the full list.
const POLL_INTERVAL_MS = 45_000;

const KIND_ICONS = {
    // Phase 48 — original assignment kinds
    application_assigned: FileText,
    lead_assigned:        Sparkles,
    complaint_assigned:   MessageSquareWarning,
    survey_assigned:      MapPin,
    visit_assigned:       ClipboardCheck,
    // Phase 47.45 — Payment lifecycle
    payment_recorded:         IndianRupee,
    payment_edited:           Pencil,
    payment_voided:           Ban,
    payment_request_new:      ShieldCheck,
    payment_request_approved: ThumbsUp,
    payment_request_rejected: ThumbsDown,
    // Phase 47.45 — Lead / Application / Complaint updates
    lead_status_changed:          ArrowRightCircle,
    lead_stage_changed:           ArrowRightCircle,
    lead_label_updated:           Tag,
    application_status_changed:   ArrowRightCircle,
    application_doc_uploaded:     Upload,
    complaint_status_changed:     ArrowRightCircle,
    complaint_resolved:           CheckCircle2,
    // Phase 47.46 — Phase C: Site Survey terminal states + Project auto-create
    survey_completed:             CheckCircle2,
    survey_cancelled:             Ban,
    project_created:              Rocket,
    project_updated:              ArrowRightCircle,
    // Phase 47.46 — Phase D: Attendance / Broadcast / System
    attendance_late:              Clock,
    attendance_missed:            Clock,
    attendance_absent:            UserX,
    broadcast_news:               Megaphone,
    deletion_requested:           Trash2,
    system_alert:                 AlertTriangle,
};

// Optional dot colour override per kind, so red/rose/green cues match icon meaning.
const KIND_TINT = {
    payment_recorded:         'text-emerald-600 bg-emerald-500/15',
    payment_voided:           'text-rose-600 bg-rose-500/15',
    payment_request_new:      'text-amber-600 bg-amber-500/15',
    payment_request_approved: 'text-emerald-600 bg-emerald-500/15',
    payment_request_rejected: 'text-rose-600 bg-rose-500/15',
    complaint_resolved:       'text-emerald-600 bg-emerald-500/15',
    survey_completed:         'text-emerald-600 bg-emerald-500/15',
    survey_cancelled:         'text-rose-600 bg-rose-500/15',
    project_created:          'text-sky-600 bg-sky-500/15',
    attendance_late:          'text-amber-600 bg-amber-500/15',
    attendance_missed:        'text-amber-600 bg-amber-500/15',
    attendance_absent:        'text-rose-600 bg-rose-500/15',
    broadcast_news:           'text-violet-600 bg-violet-500/15',
    deletion_requested:       'text-rose-600 bg-rose-500/15',
    system_alert:             'text-rose-600 bg-rose-500/15',
};

// Where to go when a user taps a notification row. Falls back to a Care/Admin
// list page — never a dead link.
function _hrefFor(entity_type, entity_id, role) {
    const r = (role || '').toLowerCase();
    if (entity_type === 'application')     return `/admin/applications/${entity_id}`;
    if (entity_type === 'lead')            return `/admin/leads/${entity_id}`;
    if (entity_type === 'complaint')       return `/admin/complaints/${entity_id}`;
    if (entity_type === 'site_survey')     return `/admin/site-surveys/${entity_id}`;
    // Phase 47.45/46 — payment / project / attendance / announcements
    if (entity_type === 'payment_request') return `/admin/payment-ins`;
    if (entity_type === 'quotation')       return `/admin/quotations/${entity_id}`;
    if (entity_type === 'sale_invoice')    return `/admin/sale-invoices/${entity_id}`;
    if (entity_type === 'project')         return `/admin/projects/${entity_id}`;
    if (entity_type === 'attendance')      return `/admin/attendance-history`;
    if (entity_type === 'broadcast_news')  return `/admin/broadcast-news`;
    if (entity_type === 'deletion_request') return `/admin/deletion-requests`;
    return '/admin';
}

function _timeAgo(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const s = Math.round((Date.now() - d.getTime()) / 1000);
        if (s < 45)      return 'just now';
        if (s < 90)      return '1 min ago';
        if (s < 3600)    return `${Math.round(s / 60)} min ago`;
        if (s < 7200)    return '1 hr ago';
        if (s < 86400)   return `${Math.round(s / 3600)} hr ago`;
        if (s < 172800)  return 'yesterday';
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    } catch {
        return '';
    }
}

export function NotificationBell({ lang = 'en' }) {
    const { user } = useAuth() || {};
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);
    const isHi = lang === 'hi';

    const refreshCount = useCallback(async () => {
        try {
            const r = await api.unreadNotifCount();
            setUnread(r?.unread_count || 0);
        } catch {/* silent */}
    }, []);

    const refreshList = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.listNotifications({ limit: 20 });
            setRows(Array.isArray(r?.rows) ? r.rows : []);
            setUnread(r?.unread_count || 0);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);

    // ── Poll count while the app is open ──
    useEffect(() => {
        if (!user?.id) return;
        refreshCount();
        pollRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [user?.id, refreshCount]);

    // ── When the popover opens, load the list ──
    useEffect(() => {
        if (open) refreshList();
    }, [open, refreshList]);

    const handleRowClick = useCallback(async (row) => {
        try {
            if (!row?.is_read) {
                await api.markNotifRead(row.id);
                setRows(prev => prev.map(r => r.id === row.id ? { ...r, is_read: true, read_at: new Date().toISOString() } : r));
                setUnread(u => Math.max(0, u - 1));
            }
        } catch { /* still navigate */ }
        setOpen(false);
        const href = _hrefFor(row?.entity_type, row?.entity_id, user?.role);
        if (href) navigate(href);
    }, [navigate, user?.role]);

    const handleMarkAll = useCallback(async () => {
        try {
            await api.markAllNotifRead();
            setRows(prev => prev.map(r => ({ ...r, is_read: true, read_at: new Date().toISOString() })));
            setUnread(0);
            toast.success(isHi ? 'सभी notifications पढ़े गए' : 'All notifications marked read');
        } catch (e) {
            toast.error(e?.friendlyMessage || 'Failed to mark all read');
        }
    }, [isHi]);

    const badgeText = useMemo(() => unread > 99 ? '99+' : (unread > 0 ? String(unread) : ''), [unread]);

    if (!user?.id) return null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant='ghost'
                    size='sm'
                    className='relative h-9 w-9 p-0'
                    aria-label={isHi ? 'सूचनाएँ' : 'Notifications'}
                    data-testid='notification-bell-btn'
                    title={isHi ? 'नई assignments और सूचनाएँ' : 'New assignments & alerts'}
                >
                    <Bell className='h-[18px] w-[18px]' />
                    {unread > 0 && (
                        <span
                            data-testid='notification-unread-badge'
                            className={cn(
                                'absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full',
                                'text-[10px] leading-[16px] font-bold px-1 text-white',
                                'bg-red-600 border border-white shadow-sm text-center'
                            )}
                        >
                            {badgeText}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align='end'
                sideOffset={8}
                className='w-[360px] sm:w-[380px] p-0 overflow-hidden'
                data-testid='notification-popover'
            >
                <div className='flex items-center justify-between px-3 py-2.5 border-b border-border/70 bg-muted/40'>
                    <div className='flex items-center gap-2'>
                        <Bell className='h-4 w-4 text-primary' />
                        <div className='font-semibold text-sm'>
                            {isHi ? 'सूचनाएँ' : 'Notifications'}
                        </div>
                        {unread > 0 && (
                            <Badge variant='secondary' className='h-5 text-[10px] px-1.5' data-testid='notification-unread-count'>
                                {unread} {isHi ? 'नई' : 'new'}
                            </Badge>
                        )}
                    </div>
                    {unread > 0 && (
                        <Button
                            variant='ghost'
                            size='sm'
                            className='h-7 px-2 text-xs'
                            onClick={handleMarkAll}
                            data-testid='notification-mark-all-btn'
                        >
                            <CheckCheck className='h-3.5 w-3.5 mr-1' />
                            {isHi ? 'सब पढ़े' : 'Mark all'}
                        </Button>
                    )}
                </div>

                <ScrollArea className='max-h-[380px]'>
                    {loading && rows.length === 0 ? (
                        <div className='px-4 py-6 text-center text-sm text-muted-foreground' data-testid='notification-loading'>
                            {isHi ? 'लोड हो रहा है…' : 'Loading…'}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className='px-4 py-8 text-center' data-testid='notification-empty'>
                            <Bell className='h-8 w-8 text-muted-foreground/40 mx-auto mb-2' />
                            <div className='text-sm font-medium text-foreground'>
                                {isHi ? 'कोई सूचना नहीं' : 'No notifications yet'}
                            </div>
                            <div className='text-xs text-muted-foreground mt-1'>
                                {isHi
                                    ? 'नई assignments यहाँ दिखेंगी।'
                                    : 'New assignments will show up here.'}
                            </div>
                        </div>
                    ) : (
                        <ul className='divide-y divide-border/60'>
                            {rows.map(row => {
                                const Icon = KIND_ICONS[row.kind] || Bell;
                                const tint = KIND_TINT[row.kind];
                                const title = (isHi ? row.title_hi : row.title_en) || row.title_en || row.title_hi || '';
                                return (
                                    <li key={row.id}>
                                        <button
                                            type='button'
                                            onClick={() => handleRowClick(row)}
                                            className={cn(
                                                'w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors',
                                                'hover:bg-muted/60 focus:bg-muted/60 focus:outline-none',
                                                !row.is_read && 'bg-primary/5'
                                            )}
                                            data-testid={`notification-row-${row.id}`}
                                        >
                                            <span className={cn(
                                                'shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center',
                                                row.is_read
                                                    ? 'bg-muted text-muted-foreground'
                                                    : (tint || 'bg-primary/15 text-primary')
                                            )}>
                                                <Icon className='h-3.5 w-3.5' />
                                            </span>
                                            <span className='min-w-0 flex-1'>
                                                <span className='flex items-center gap-2'>
                                                    <span className='font-medium text-[13px] leading-tight truncate'>
                                                        {title}
                                                    </span>
                                                    {!row.is_read && (
                                                        <span className='shrink-0 h-1.5 w-1.5 rounded-full bg-red-500'
                                                              data-testid={`notification-dot-${row.id}`} />
                                                    )}
                                                </span>
                                                {row.body && (
                                                    <span className='block text-[12px] text-muted-foreground line-clamp-2 mt-0.5'>
                                                        {row.body}
                                                    </span>
                                                )}
                                                <span className='flex items-center gap-2 mt-1 text-[11px] text-muted-foreground/80'>
                                                    <span className='inline-flex items-center gap-1'>
                                                        <span className='font-mono uppercase tracking-wide'>
                                                            {row.entity_short}
                                                        </span>
                                                    </span>
                                                    <span>·</span>
                                                    <span>{_timeAgo(row.created_at)}</span>
                                                    {row.actor_name && (
                                                        <>
                                                            <span>·</span>
                                                            <span className='truncate max-w-[120px]'>
                                                                {isHi ? 'से' : 'by'} {row.actor_name}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                            </span>
                                            {!row.is_read && (
                                                <Check className='h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100' />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </ScrollArea>

                <div className='px-3 py-2 border-t border-border/70 bg-muted/30 text-center'>
                    <span className='text-[11px] text-muted-foreground'>
                        {isHi ? 'हर 45 सेकंड में auto-refresh' : 'Auto-refreshes every 45s'}
                    </span>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default NotificationBell;
